<template>
  <SideBarLayoutContent side-bar-title="Filter">
    <template v-slot:top-bar="{ toggleSheet, showSheet }">
      <h1 class="text-headline-large grow">{{ $t('mainNavigation.feedbackHistory') }}</h1>
      <div class="relative">
        <BaseIconButton
          :is-selectable="true"
          :selected="showSheet"
          color="tonal"
          icon="tune"
          @click="toggleSheet"
        />
        <div
          v-if="hasFilter"
          class="bg-error absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full"
        ></div>
      </div>
    </template>
    <PersistantLoadingWrapper :state="status">
      <div v-if="data">
        <section
          v-for="group in groupedFeedbacks"
          :key="group.key"
          class="mb-6 flex flex-col gap-1 last:mb-0"
        >
          <h2
            v-if="group.key != 'older' || groupedFeedbacks.length > 1"
            class="text-title-small text-on-surface-variant px-1"
          >
            {{ group.title }}
          </h2>
          <ul class="flex flex-col gap-0.5">
            <li
              v-for="feedback of group.feedbacks"
              :key="feedback.id"
              class="bg-surface-container rounded-extra-small first:rounded-t-large last:rounded-b-large overflow-hidden"
            >
              <FeedbackSurveyHistoryItem
                v-if="feedback.type == 'structured'"
                :feedback="feedback"
              />
              <FeedbackSpontaneousHistoryItem v-else :feedback="feedback" />
            </li>
          </ul>
        </section>
        <EmptyPage
          v-if="!data.length"
          :title="$t('mainNavigation.feedbackHistory')"
          :description="$t('feedbackHistory.empty.description')"
          icon="history"
        >
          <BaseTextButton
            v-if="hasFilter"
            color="tonal"
            icon="filter_alt_off"
            size="M"
            @click="resetFilters"
            >Filter zurücksetzen</BaseTextButton
          >
        </EmptyPage>
      </div>
    </PersistantLoadingWrapper>
    <template #side-bar>
      <div class="flex flex-col gap-6">
        <div class="flex flex-col">
          <h4 class="text-title-small text-on-surface-variant mb-1 px-6">Personen</h4>
          <BaseHorizontalOverflow :show-buttons="true">
            <ul class="flex w-fit flex-row px-6">
              <li v-for="member in allMembers" :key="member.id">
                <div
                  class="group flex cursor-pointer flex-col items-center"
                  @click="toggleMemberId(member.id)"
                >
                  <div class="relative overflow-hidden rounded-full select-none">
                    <BaseMemberAvatar :member="member" size="64" />
                    <div
                      v-if="filterParameter.memberIds.includes(member.id)"
                      v-tooltip.bottom="memberFullName(member)"
                      class="bg-primary/70 absolute inset-0 flex h-full w-full items-center justify-center text-white"
                    >
                      <BaseIcon class="text-on-primary">check</BaseIcon>
                    </div>
                    <BaseStateLayer type="primary" />
                  </div>
                  <span
                    class="text-label-small text-on-surface-variant w-18 overflow-hidden px-0.5 text-center text-ellipsis whitespace-nowrap"
                    >{{ memberShortName(member) }}</span
                  >
                </div>
              </li>
            </ul>
          </BaseHorizontalOverflow>
        </div>
        <div class="flex flex-col px-6">
          <h4 class="text-title-small text-on-surface-variant mb-1">Perspektive</h4>
          <div class="flex flex-row flex-wrap gap-1">
            <BaseChip
              type="filter"
              icon="arrow_left_alt"
              @click="toggleSent"
              :selected="filterParameter.sent"
              >Gesendet</BaseChip
            >
            <BaseChip
              icon="arrow_right_alt"
              type="filter"
              @click="toggleReceived"
              :selected="filterParameter.received"
              >Empfangen</BaseChip
            >
          </div>
        </div>
        <div class="flex flex-col px-6">
          <h4 class="text-title-small text-on-surface-variant mb-1">Typ</h4>
          <div class="flex flex-row flex-wrap gap-1">
            <BaseChip
              type="filter"
              @click="toggleSpontaneous"
              :selected="filterParameter.spontaneous"
              >Spontan</BaseChip
            >
            <BaseChip type="filter" @click="toggleStructured" :selected="filterParameter.structured"
              >Strukturiert</BaseChip
            >
          </div>
        </div>
        <div class="flex flex-col px-6">
          <hr class="text-outline-variant mb-4 w-full" />
          <BaseTextButton
            class="self-start"
            color="outlined"
            size="XS"
            @click="resetFilters"
            icon="filter_alt_off"
            >Filter zurücksetzen</BaseTextButton
          >
        </div>
      </div>
    </template>
  </SideBarLayoutContent>
</template>

<script setup lang="ts">
  import SideBarLayoutContent from '@/core/components/layouts/SideBarLayoutContent.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseChip from '../../../core/components/base/chip/BaseChip.vue'
  import { useFeedbackFilter } from '../composables/useFeedbackFilter'
  import PersistantLoadingWrapper from '../../../core/components/loading/PersistantLoadingWrapper.vue'
  import { useMembersStore } from '../../../stores/members.store'
  import BaseMemberAvatar from '../../../core/components/base/avatar/BaseMemberAvatar.vue'
  import { useUserStore } from '../../../stores/user.store'
  import BaseStateLayer from '../../../core/components/base/state-layer/BaseStateLayer.vue'
  import BaseIcon from '../../../core/components/base/icon/BaseIcon.vue'
  import BaseHorizontalOverflow from '../../../core/components/base/horizontal-overflow/BaseHorizontalOverflow.vue'
  import { type FeedbackHistory, useFeedbackHistory } from '../composables/useFeedbackHistory'
  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import FeedbackSurveyHistoryItem from '../components/feedback-history-item/FeedbackSurveyHistoryItem.vue'
  import FeedbackSpontaneousHistoryItem from '../components/feedback-history-item/FeedbackSpontaneousHistoryItem.vue'
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import EmptyPage from '@/core/components/empty-states/EmptyPage.vue'
  import { memberFullName, memberShortName } from '@/core/models/member/member-name.ts'

  const { status, data, load } = useFeedbackHistory()

  const {
    filterParameter,
    toggleSpontaneous,
    toggleStructured,
    toggleReceived,
    toggleSent,
    toggleMemberId,
    resetFilters,
    hasFilter,
  } = useFeedbackFilter((params) => load({ query: params }))

  const memberStore = useMembersStore()
  const userStore = useUserStore()

  const allMembers = memberStore.members.filter((member) => member.id != userStore.user.id)
  type FeedbackHistoryItem = FeedbackHistory[number]

  const { t } = useI18n()

  const groupedFeedbacks = computed(() => {
    if (!data.value) {
      return []
    }

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)

    const grouped = {
      today: [] as FeedbackHistoryItem[],
      yesterday: [] as FeedbackHistoryItem[],
      older: [] as FeedbackHistoryItem[],
    }

    for (const feedback of data.value) {
      const updatedAt = new Date(feedback.updatedAt)
      const updatedAtDate = new Date(
        updatedAt.getFullYear(),
        updatedAt.getMonth(),
        updatedAt.getDate(),
      )

      if (updatedAtDate.getTime() === today.getTime()) {
        grouped.today.push(feedback)
        continue
      }

      if (updatedAtDate.getTime() === yesterday.getTime()) {
        grouped.yesterday.push(feedback)
        continue
      }

      grouped.older.push(feedback)
    }

    return [
      { key: 'today', title: t('common.today'), feedbacks: grouped.today },
      { key: 'yesterday', title: t('common.yesterday'), feedbacks: grouped.yesterday },
      { key: 'older', title: t('common.older'), feedbacks: grouped.older },
    ].filter((group) => group.feedbacks.length > 0)
  })
</script>
